import React, { useEffect, useMemo, useState } from 'react';

export default function BillableHoursCard({ authAxios }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0); // 0 = current, 1 = previous week, etc.

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      const token = localStorage.getItem('token');
      if (!token) {
        // Not authenticated (or just logged out) — don't call the API.
        if (!cancelled) {
          setLoading(false);
          setError(null);
        }
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const resp = await authAxios.get(`reports/billable-hours-7d/?week_offset=${weekOffset}`);
        if (!cancelled) setData(resp.data);
      } catch (e) {
        // Ignore abort/cancel noise (commonly triggered by logout).
        if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') {
          return;
        }
        if (!cancelled) setError('Failed to load billable hours');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [authAxios, weekOffset]);

  const days = data?.days || [];
  const maxHours = useMemo(() => days.reduce((m, d) => Math.max(m, d.billable_hours || 0), 0), [days]);
  const total = data?.total_billable_hours || 0;
  const canPrev = data?.can_go_prev ?? true; // default allow until API responds
  const canNext = data?.can_go_next ?? (weekOffset > 0);

  const weekdayLabel = (isoDate) => {
    const d = new Date(isoDate + 'T00:00:00');
    return d.toLocaleDateString([], { weekday: 'short' });
  };

  return (
    <div className="dashboard-widget col-span-2" aria-live="polite">
      <div className="flex items-center justify-between">
        <h3 className="widget-title">Billable Hours</h3>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-600" aria-label={`Total billable hours this week: ${total}`}>
            Total: <strong>{total}</strong> hrs
          </span>
          <div className="inline-flex rounded-md shadow-sm" role="group" aria-label="Week navigation">
            <button
              type="button"
              className={`px-2 py-1 rounded-l-md ring-1 ring-inset ring-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed`}
              onClick={() => setWeekOffset((o) => o + 1)}
              disabled={!canPrev}
              aria-label="Previous week"
              title="Previous week"
            >
              ‹
            </button>
            <button
              type="button"
              className={`px-2 py-1 -ml-px rounded-r-md ring-1 ring-inset ring-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed`}
              onClick={() => setWeekOffset((o) => Math.max(0, o - 1))}
              disabled={!canNext}
              aria-label="Next week"
              title="Next week"
            >
              ›
            </button>
          </div>
        </div>
      </div>
      {loading ? (
        <div className="p-6 h-48 flex items-center justify-center" role="status" aria-label="Loading billable hours">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
          <span className="sr-only">Loading...</span>
        </div>
      ) : error ? (
        <div className="p-6 h-48 flex items-center justify-center">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      ) : (
        <div className="p-4 h-64 flex items-end justify-between gap-2">
          {days.map((d) => {
            const pct = maxHours > 0 ? Math.max(4, Math.round((d.billable_hours / maxHours) * 100)) : 4; // min 4%
            const label = `${weekdayLabel(d.date)}: ${d.billable_hours} hours`;
            return (
              <div key={d.date} className="flex flex-col items-center flex-1 group h-full" aria-label={label}>
                <div className="relative h-full flex items-end justify-center w-full">
                  <div className={`w-6 rounded-t-md ${d.billable_hours > 0 ? 'bg-blue-500' : 'bg-gray-300'}`} style={{ height: pct + '%' }} aria-hidden="true"></div>
                  {/* Tooltip */}
                  <div className="pointer-events-none absolute -top-2 -translate-y-full whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-[10px] text-white opacity-0 shadow transition-opacity group-hover:opacity-100" role="tooltip">
                    {weekdayLabel(d.date)}: {d.billable_hours}h
                  </div>
                </div>
                <p className="text-[10px] mt-1 text-gray-500" aria-hidden="true">{weekdayLabel(d.date)}</p>
              </div>
            );
          })}
        </div>
      )}
      {data && (
        <div className="px-4 pb-4 text-xs text-gray-500 flex items-center justify-between">
          <p>Range: {data.start_date} to {data.end_date}</p>
          {weekOffset > 0 && <p>Week {weekOffset} of {data.max_week_offset}</p>}
        </div>
      )}
    </div>
  );
}
